/**
 * EarlyAccessPanel - Panel de administración para gestionar acceso anticipado
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Rocket,
  Search,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  Shield,
  Calendar,
  Mail,
  UserPlus,
  RefreshCw,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { formatReleaseDate, FEATURES } from '@/lib/featureFlags';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function EarlyAccessPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [totalWithAccess, setTotalWithAccess] = useState(0);
  const [toggling, setToggling] = useState(null);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/admin/early-access/users?search=${encodeURIComponent(search)}`,
        getAuthHeaders()
      );
      setUsers(response.data.users || []);
      setTotalWithAccess(response.data.total_with_early_access || 0);
    } catch (error) {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300); // Debounce search

    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Toggle early access
  const toggleAccess = async (userId, currentAccess) => {
    setToggling(userId);
    try {
      await axios.post(
        `${API_BASE}/api/admin/early-access/toggle`,
        { user_id: userId, has_access: !currentAccess },
        getAuthHeaders()
      );
      toast.success(`Early Access ${!currentAccess ? 'otorgado' : 'revocado'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Error actualizando acceso');
    } finally {
      setToggling(null);
    }
  };

  // Bulk grant access
  const handleBulkGrant = async () => {
    if (!bulkEmails.trim()) return;

    const emails = bulkEmails
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e.includes('@'));

    if (emails.length === 0) {
      toast.error('No se encontraron emails válidos');
      return;
    }

    setBulkLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/early-access/grant-bulk`,
        emails,
        getAuthHeaders()
      );
      
      const { granted, not_found } = response.data;
      
      if (granted.length > 0) {
        toast.success(`Acceso otorgado a ${granted.length} usuarios`);
      }
      if (not_found.length > 0) {
        toast.error(`${not_found.length} emails no encontrados: ${not_found.slice(0, 3).join(', ')}${not_found.length > 3 ? '...' : ''}`);
      }
      
      setShowBulkDialog(false);
      setBulkEmails('');
      fetchUsers();
    } catch (error) {
      toast.error('Error otorgando acceso');
    } finally {
      setBulkLoading(false);
    }
  };

  // Format date
  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    return new Date(isoDate).toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-[#111111] to-[#0A0A0A] border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#34C759] to-[#007AFF] flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Early Access v2.0</CardTitle>
                <CardDescription>
                  Lanzamiento oficial: {formatReleaseDate()}
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#34C759]">{totalWithAccess}</p>
              <p className="text-xs text-[#71717A]">usuarios con acceso</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.values(FEATURES).map((feature) => (
              <div
                key={feature.id}
                className="flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/[0.05]"
              >
                <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-sm text-[#F4F4F5]">{feature.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search and Actions */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            placeholder="Buscar por email o nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#111111] border-white/10"
            data-testid="early-access-search"
          />
        </div>
        <Button
          variant="outline"
          onClick={fetchUsers}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          onClick={() => setShowBulkDialog(true)}
          className="bg-[#34C759] hover:bg-[#2DB34C] text-black"
          data-testid="bulk-grant-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Agregar Varios
        </Button>
      </div>

      {/* Users Table */}
      <Card className="bg-[#111111] border-white/10">
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#71717A]">Usuario</TableHead>
                  <TableHead className="text-[#71717A]">Plan</TableHead>
                  <TableHead className="text-[#71717A]">Acceso Otorgado</TableHead>
                  <TableHead className="text-[#71717A]">Por</TableHead>
                  <TableHead className="text-[#71717A] text-right">Early Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#71717A]" />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-[#71717A]">
                      No se encontraron usuarios
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#0A0A0A] flex items-center justify-center">
                            <Mail className="w-4 h-4 text-[#71717A]" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{user.name || 'Sin nombre'}</p>
                            <p className="text-xs text-[#71717A]">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={
                            user.subscription_plan?.includes('enterprise') 
                              ? 'border-[#AF52DE] text-[#AF52DE]'
                              : user.subscription_plan === 'pro'
                              ? 'border-[#007AFF] text-[#007AFF]'
                              : 'border-[#71717A] text-[#71717A]'
                          }
                        >
                          {user.subscription_plan || 'free'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#71717A]">
                        {user.early_access_granted_at ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.early_access_granted_at)}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-[#71717A] text-sm">
                        {user.early_access_granted_by || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {user.early_access ? (
                            <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Activo
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[#71717A]">
                              <XCircle className="w-3 h-3 mr-1" />
                              Sin acceso
                            </Badge>
                          )}
                          <Switch
                            checked={user.early_access || false}
                            onCheckedChange={() => toggleAccess(user.id, user.early_access)}
                            disabled={toggling === user.id}
                            data-testid={`toggle-access-${user.id}`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Bulk Grant Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="bg-[#111111] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserPlus className="w-5 h-5 text-[#34C759]" />
              Agregar Usuarios en Masa
            </DialogTitle>
            <DialogDescription>
              Ingresa los emails de los usuarios (uno por línea o separados por coma)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="usuario1@email.com&#10;usuario2@email.com&#10;usuario3@email.com"
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              className="min-h-[200px] bg-[#0A0A0A] border-white/10 font-mono text-sm"
              data-testid="bulk-emails-input"
            />
            <p className="text-xs text-[#71717A] mt-2">
              Los usuarios deben estar registrados en la plataforma
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleBulkGrant}
              disabled={bulkLoading || !bulkEmails.trim()}
              className="bg-[#34C759] hover:bg-[#2DB34C] text-black"
              data-testid="confirm-bulk-grant"
            >
              {bulkLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Otorgar Acceso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
